import { Providers } from '../../enums/providers';
import { UserSettings } from '../../enums/user-settings/user-settings';
import { UserSettingsOptions } from '../../enums/user-settings/user-settings-options';
import Global from '../global-selectors';

const hasUpsProviderInProviderCosts = (providerCosts = [], labelProviders = []) => {
  return providerCosts.some((cost) => {
    const provider = labelProviders.find((lp) => String(lp.sellerLabelProviderId) === String(cost.sellerLabelProviderId));
    return provider?.labelProviderId === Providers.UPSWallet.labelProviderId;
  });
};

export const hasUpsProviderInCostReview = (providers = [], walletedProviderGroups = []) => {
  return (
    providers.some((provider) => provider.labelProviderId === Providers.UPSWallet.labelProviderId) ||
    walletedProviderGroups.some((providerGroup) =>
      providerGroup.some((provider) => provider.labelProviderId === Providers.UPSWallet.labelProviderId),
    )
  );
};

export const INSUFFICIENT_BALANCE_ERROR_CODE = 'InsufficientBalance';
// shipsurance purchase failure error? "Failed to purchase Shipsurance" is not in BE codebase
const INSUFFICIENT_SHIPSURANCE_BALANCE_CODE = 'InsufficientShipsuranceBalance';
const UPS_TERMS_NOT_ACCEPTED_CODE = 'UpsTermsNotAccepted';
export const UPS_INVOICED_NOTIFICATION_ERROR_CODE = 'UpsInvoicedNotificationError';
export const CODES_THAT_SHOW_COST_REVIEW = [
  INSUFFICIENT_BALANCE_ERROR_CODE,
  INSUFFICIENT_SHIPSURANCE_BALANCE_CODE,
  UPS_TERMS_NOT_ACCEPTED_CODE,
  UPS_INVOICED_NOTIFICATION_ERROR_CODE,
];

export const hasShowErrorsEnabled = (userQuickshipSettings = []) => {
  return userQuickshipSettings.find(({ settingName }) => settingName === UserSettings.quickshipShowErrors.settingName)?.settingValue;
};
export const hasShowWarningsEnabled = (userQuickshipSettings = []) => {
  return userQuickshipSettings.find(({ settingName }) => settingName === UserSettings.quickshipShowWarnings.settingName)?.settingValue;
};
export const hasShowPrintdialogPreviewEnabled = (userQuickshipSettings = []) => {
  return userQuickshipSettings.find(({ settingName }) => settingName === UserSettings.quickshipShowPrintDialogPreview.settingName)
    ?.settingValue;
};
export const hasShowLabelBatchStatusEnabled = (userQuickshipSettings = []) => {
  return userQuickshipSettings.find(({ settingName }) => settingName === UserSettings.quickshipShowLabelBatchStatus.settingName)
    ?.settingValue;
};

export const determineShouldShowCostReviewByError = (errorResponse = [], { shouldUseQuickship, userQuickshipSettings }) => {
  const userQuickshipShowCostSummarySetting =
    userQuickshipSettings?.find((setting) => setting.settingName === UserSettings.quickshipShowCostSummary.settingName) || {};
  if (
    shouldUseQuickship &&
    userQuickshipShowCostSummarySetting?.settingValue === UserSettingsOptions.quickshipAlwaysShowCostSummary.settingValue
  ) {
    return true;
  }

  const hasErrorCodeForCostReview =
    errorResponse.length === 1 && errorResponse.some((error) => CODES_THAT_SHOW_COST_REVIEW.includes(error.errorCode));

  if (
    !shouldUseQuickship ||
    userQuickshipShowCostSummarySetting?.settingValue === UserSettingsOptions.quickshipShowCostSummaryIfNeeded.settingValue
  ) {
    return hasErrorCodeForCostReview;
  }

  return false;
};

export const determineShouldShowCostReviewByCostReviewBalances = (
  { providerCosts = [], insuranceCosts = [], upsTermsRequired = false /* costWarnings = [] */ },
  { shouldUseQuickship, userQuickshipSettings, isUpsTermsEnabled = false, labelProviders = [] },
  defaultValue = true,
) => {
  // Only consider upsTermsRequired if the feature flag is enabled and there's actually a UPS provider in the order
  const hasUpsProvider = hasUpsProviderInProviderCosts(providerCosts, labelProviders);
  const shouldShowForUpsTerms = isUpsTermsEnabled && upsTermsRequired && hasUpsProvider;

  if (!shouldUseQuickship) {
    return defaultValue || shouldShowForUpsTerms;
  }

  const userQuickshipShowCostSummarySetting =
    userQuickshipSettings.find((setting) => setting.settingName === UserSettings.quickshipShowCostSummary.settingName) || {};

  if (userQuickshipShowCostSummarySetting?.settingValue === UserSettingsOptions.quickshipAlwaysShowCostSummary.settingValue) {
    return true;
  }

  if (userQuickshipShowCostSummarySetting?.settingValue === UserSettingsOptions.quickshipNeverShowCostSummary.settingValue) {
    return shouldShowForUpsTerms;
  }

  // calculate
  const providersNeedCostReview =
    providerCosts.filter((provider) => !!provider.walletId && provider.subTotal > provider.balance).length > 0;
  const insuranceNeedsCostReview = insuranceCosts.filter((ins) => ins.subTotal > ins.balance).length > 0;
  // cost warnings?
  return providersNeedCostReview || insuranceNeedsCostReview || shouldShowForUpsTerms;
};

export const determineShouldShowPrintDialog = ({ shouldUseQuickship, userQuickshipSettings }) => {
  if (shouldUseQuickship && !hasShowPrintdialogPreviewEnabled(userQuickshipSettings)) {
    return false;
  }

  return true;
};

export const determineShouldShowErrorsOrWarnings = ({ shouldUseQuickship, userQuickshipSettings }) => {
  if (!shouldUseQuickship) {
    return true;
  }

  return hasShowErrorsEnabled(userQuickshipSettings) || hasShowWarningsEnabled(userQuickshipSettings);
};

export const determineShouldShowLabelBatchStatus = ({ shouldUseQuickship, userQuickshipSettings }) => {
  if (!shouldUseQuickship) {
    return true;
  }

  return hasShowLabelBatchStatusEnabled(userQuickshipSettings);
};

export const getQuickshipPropsFromState = (state) => {
  const shouldUseQuickship = Global.selectSellerQuickshipAllowedForSeller(state) && Global.selectUserIsQuickshipEnabledForUser(state);
  const userQuickshipSettings = Global.selectUserUserQuickshipSettings(state);
  return { shouldUseQuickship, userQuickshipSettings };
};

export const shouldLaunchLbsModal = (quickshipProps) => {
  const userQuickshipShowCostSummarySetting = quickshipProps.userQuickshipSettings.find(
    (setting) => setting.settingName === UserSettings.quickshipShowCostSummary.settingName,
  ) || {
    settingValue: UserSettingsOptions.quickshipNeverShowCostSummary.settingValue,
  };
  return (
    determineShouldShowErrorsOrWarnings(quickshipProps) ||
    userQuickshipShowCostSummarySetting?.settingValue !== UserSettingsOptions.quickshipNeverShowCostSummary.settingValue ||
    determineShouldShowLabelBatchStatus(quickshipProps)
  );
};
